import { Button, Dialog } from "@mui/material";
import { useEffect, useState } from "react";
import useAuth from "../hooks/useAuth";
import { calculateTheAdjustmentPriceByType } from "../../utils/summaryCalculation";
import useConfirm from "../hooks/useConfirm";
import { useTheme } from "../hooks/useTheme";
import colorConfigs from "../../configs/colorConfigs";
import { deleteAdditionalChargeApi, editNewAdditionalCharge } from "../../services/rental";
import Cookies from "js-cookie";
import useTenant from "../hooks/useTenant";


type EditAdjustmentModalProps = {
    editAdjustmentIsOpen: boolean;
    setEditAdjustmentIsOpen: (value: boolean) => void;
    modalData: any;
    setModalData: (value: any) => void;
    extraValue: any[];
    summaryData: any;
    isCompletedOrCancelled: boolean;
    refreshTheSummary: () => void;
    reservationId: number;
    setPleaseWait: (value: boolean) => void;
}

const EditAdjustmentModal = ({
    editAdjustmentIsOpen,
    setEditAdjustmentIsOpen,
    modalData,
    setModalData,
    extraValue,
    summaryData,
    isCompletedOrCancelled,
    refreshTheSummary,
    reservationId,
    setPleaseWait
}: EditAdjustmentModalProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [adjustmentName, setAdjustmentName] = useState<string>("");
    const [quantity, setQuantity] = useState<number>(0);
    const [price, setPrice] = useState<string>('');
    const [comment, setComment] = useState<string>("");
    const [selectionType, setSelectionType] = useState<string>("");
    const [chargeType, setChargeType] = useState<string>("");
    const [additionalChargeId, setAdditionalChargeId] = useState<number>(0);

    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";


    useEffect(() => {
        if (editAdjustmentIsOpen && modalData) {
            const selectedAdjustmentAdditionalCharge = extraValue?.find((item: any) =>
                item?.additionalCharges?.some((data: any) => data?.reservationAdditionalId === modalData?.id)
            )?.additionalCharges?.find((data: any) => data?.reservationAdditionalId === modalData?.id);

            if (selectedAdjustmentAdditionalCharge) {
                setSelectionType(selectedAdjustmentAdditionalCharge?.selectionType);
                setChargeType(selectedAdjustmentAdditionalCharge?.chargeType || 0);
                setAdditionalChargeId(selectedAdjustmentAdditionalCharge?.id || 0);

                let totalDays = 0;
                totalDays = summaryData ? (summaryData?.totalMonths * 30) + (summaryData?.totalWeeks * 7) + summaryData?.totalDays + (summaryData?.totalHours > 0 ? 1 : 0) : 0;

                const basePrice = calculateTheAdjustmentPriceByType(
                    selectedAdjustmentAdditionalCharge,
                    summaryData?.totalBasePriceBeforeDiscount,
                    totalDays
                );

                setPrice(basePrice?.toString());
            } else {
                setSelectionType('');
                setChargeType('');
                setPrice('');
            }

            setAdjustmentName(modalData?.additionalChargeName);
            setQuantity(modalData?.quantity);
            setComment(modalData?.comment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editAdjustmentIsOpen])

    const resetTheForm = () => {
        setAdjustmentName('');
        setQuantity(0);
        setPrice('');
        setComment('');
        setSelectionType('');
        setChargeType('');
        setEditAdjustmentIsOpen(false);
        setModalData(null);
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isCompletedOrCancelled) {
            setPleaseWait(true);

            editNewAdditionalCharge(
                reservationId,
                modalData?.id,
                additionalChargeId,
                Number(price),
                modalData?.commentId,
                comment?.trim(),
                auth?.id,
                tenantId
            )
                .then(() => {
                    resetTheForm();
                    refreshTheSummary();
                    handleTenantError(null);
                })
                .catch((error => {
                    setPleaseWait(false);
                    handleTenantError(error);
                }))
        }
    }

    const deleteAdjustment = async () => {
        if (!isCompletedOrCancelled) {
            setConfirmData({
                title: 'Delete Adjustment',
                summary: "Are you sure you want to delete this adjustment?",
                confirmBtn: 'Yes',
                isConfirm: async (callback: any) => {
                    setPleaseWait(true);

                    await deleteAdditionalChargeApi(
                        reservationId,
                        modalData?.id,
                        auth?.id,
                        tenantId
                    )
                        .then(() => {
                            resetTheForm();
                            refreshTheSummary();
                            handleTenantError(null);
                        })
                        .catch(error => {
                            setPleaseWait(false);
                            handleTenantError(error);
                        });

                    callback();
                }
            });

            setConfirmPopup(true);
        }
    }

    return (
        <>
            <Dialog
                open={editAdjustmentIsOpen}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '500px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => setEditAdjustmentIsOpen(false)}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Edit adjustment</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <form onSubmit={handleSubmit}>
                                            <div className="grid grid-cols-2 gap-6">
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Charge of Adjustment</label>
                                                        <br />
                                                        <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{adjustmentName}</span>
                                                    </div>
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Quantity</label>
                                                        <br />
                                                        <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{quantity}</span>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}> {chargeType} {" "} {selectionType === "Only One" ? " One Time" : "/ Day"}</label>
                                                        <input
                                                            type="text"
                                                            className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                            value={price}
                                                            required
                                                            onChange={(e) => setPrice(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="w-full mt-4">
                                                <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Comments</label>
                                                <textarea
                                                    className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    rows={6}
                                                    value={comment}
                                                    onChange={(e) => setComment(e.target.value)}
                                                />
                                            </div>

                                            <div className="flex gap-4 mt-6">
                                                {
                                                    !isCompletedOrCancelled && (

                                                        <Button variant="contained"
                                                            sx={{
                                                                backgroundColor: colorConfigs.btn.main.bg
                                                            }}
                                                            type="submit"
                                                        >
                                                            Save
                                                        </Button>
                                                    )
                                                }
                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => {
                                                        setEditAdjustmentIsOpen(false);
                                                    }}
                                                >
                                                    Cancel
                                                </Button>

                                                {
                                                    !isCompletedOrCancelled && (
                                                        <Button variant="contained"
                                                            sx={{
                                                                backgroundColor: colorConfigs.btn.delete.bg
                                                            }}
                                                            onClick={() => deleteAdjustment()}
                                                        >
                                                            Delete
                                                        </Button>
                                                    )
                                                }
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default EditAdjustmentModal;